import {
  Accordion,
  AccordionButton,
  AccordionIcon,
  AccordionItem,
  AccordionPanel,
  Badge,
  Box,
  Divider,
  HStack,
  Icon,
  Link,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Skeleton,
  Text,
  VStack,
} from '@elkaso-app/web-design';
import { useGetDedDetailByIdApi } from 'apis/crm-ded-details';
import React from 'react';
import { FiCalendar, FiExternalLink, FiHome, FiMail, FiMapPin, FiPhone } from 'react-icons/fi';

interface ViewDetailModalProps {
  isOpen: boolean;
  onClose: () => void;
  detailId: number;
  businessName: string;
}

interface DetailRowProps {
  label: string;
  value: React.ReactNode;
  isLoading?: boolean;
  icon?: React.ElementType;
}

const DetailRow: React.FC<DetailRowProps> = ({ label, value, isLoading, icon }) => (
  <VStack align='start' spacing={1}>
    <HStack spacing={2}>
      {icon && <Icon as={icon} color='gray.500' size='sm' />}
      <Text fontSize='sm' fontWeight='semibold' color='gray.600'>
        {label}
      </Text>
    </HStack>
    {isLoading ? (
      <Skeleton height='20px' width='200px' />
    ) : (
      <Text fontSize='md' color='gray.800' pl={icon ? '24px' : '0'}>
        {value || 'Not available'}
      </Text>
    )}
  </VStack>
);

export const ViewDetailModal: React.FC<ViewDetailModalProps> = ({ isOpen, onClose, detailId, businessName }) => {
  const { data: detailData, isLoading } = useGetDedDetailByIdApi(detailId, {
    enabled: isOpen && !!detailId,
  });

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not available';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      });
    } catch {
      return 'Invalid date';
    }
  };

  const getStatusColor = (status?: string) => {
    if (!status) return 'gray';
    if (status.toLowerCase().includes('active')) return 'green';
    if (status.toLowerCase().includes('inactive')) return 'red';
    return 'yellow';
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size='4xl'>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <VStack align='start' spacing={1}>
            <Text>DED License Details</Text>
            <Text fontSize='md' fontWeight='normal' color='gray.600'>
              {businessName}
            </Text>
          </VStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody pb={6}>
          <VStack spacing={6} align='stretch'>
            {/* Basic Business Information */}
            <Box>
              <Text fontSize='lg' fontWeight='bold' mb={4} color='blue.600'>
                Business Information
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <DetailRow label='Business Name' value={detailData?.businessName} icon={FiHome} isLoading={isLoading} />
                <DetailRow label='English Name' value={detailData?.englishName} icon={FiHome} isLoading={isLoading} />
                <DetailRow label='Arabic Name' value={detailData?.arabicName} isLoading={isLoading} />
                <DetailRow label='Legal Type' value={detailData?.legalType} isLoading={isLoading} />
              </SimpleGrid>
            </Box>

            <Divider />

            {/* License Information */}
            <Box>
              <Text fontSize='lg' fontWeight='bold' mb={4} color='green.600'>
                License Information
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <DetailRow
                  label='License Number'
                  value={
                    <HStack>
                      <Text fontWeight='medium'>{detailData?.licenseNumber}</Text>
                      {detailData?.pageUrl && (
                        <Link href={detailData.pageUrl} isExternal color='blue.500'>
                          <Icon as={FiExternalLink} />
                        </Link>
                      )}
                    </HStack>
                  }
                  isLoading={isLoading}
                />
                <DetailRow
                  label='License Status'
                  value={
                    <Badge colorScheme={getStatusColor(detailData?.licenseStatus)} variant='subtle'>
                      {detailData?.licenseStatus || 'Unknown'}
                    </Badge>
                  }
                  isLoading={isLoading}
                />
                <DetailRow
                  label='Expiry Date'
                  value={formatDate(detailData?.expiryDate)}
                  icon={FiCalendar}
                  isLoading={isLoading}
                />
                <DetailRow label='Activities' value={detailData?.activities} isLoading={isLoading} />
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Contact Information */}
            <Box>
              <Text fontSize='lg' fontWeight='bold' mb={4} color='purple.600'>
                Contact Information
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <DetailRow
                  label='Mobile'
                  value={
                    detailData?.mobile ? (
                      <Link href={`tel:${detailData.mobile}`} color='blue.500'>
                        {detailData.mobile}
                      </Link>
                    ) : (
                      'Not available'
                    )
                  }
                  icon={FiPhone}
                  isLoading={isLoading}
                />
                <DetailRow
                  label='Telephone'
                  value={
                    detailData?.telephone ? (
                      <Link href={`tel:${detailData.telephone}`} color='blue.500'>
                        {detailData.telephone}
                      </Link>
                    ) : (
                      'Not available'
                    )
                  }
                  icon={FiPhone}
                  isLoading={isLoading}
                />
                <DetailRow
                  label='Email'
                  value={
                    detailData?.email ? (
                      <Link href={`mailto:${detailData.email}`} color='blue.500'>
                        {detailData.email}
                      </Link>
                    ) : (
                      'Not available'
                    )
                  }
                  icon={FiMail}
                  isLoading={isLoading}
                />
                <DetailRow label='Fax' value={detailData?.fax} isLoading={isLoading} />
                <DetailRow label='PO Box' value={detailData?.poBox} isLoading={isLoading} />
              </SimpleGrid>
            </Box>

            <Divider />

            {/* Location Information */}
            <Accordion allowToggle>
              <AccordionItem>
                <AccordionButton>
                  <Box flex='1' textAlign='left'>
                    <Text fontSize='lg' fontWeight='bold' color='orange.600'>
                      Location Information
                    </Text>
                  </Box>
                  <AccordionIcon />
                </AccordionButton>
                <AccordionPanel pb={4}>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <DetailRow label='Address' value={detailData?.address} icon={FiMapPin} isLoading={isLoading} />
                    <DetailRow label='Area' value={detailData?.area} isLoading={isLoading} />
                    <DetailRow label='Sub Area' value={detailData?.subArea} isLoading={isLoading} />
                    <DetailRow label='Community' value={detailData?.community} isLoading={isLoading} />
                    <DetailRow label='Landmark' value={detailData?.landmark} isLoading={isLoading} />
                    <DetailRow label='Street' value={detailData?.street} isLoading={isLoading} />
                    <DetailRow label='Building' value={detailData?.building} isLoading={isLoading} />
                    <DetailRow label='Floor' value={detailData?.floor} isLoading={isLoading} />
                    <DetailRow label='Unit' value={detailData?.unit} isLoading={isLoading} />
                    <DetailRow label='Parcel ID' value={detailData?.parcelId} isLoading={isLoading} />
                    {detailData?.gpsCoordinates && (
                      <DetailRow
                        label='GPS Coordinates'
                        value={
                          <Link
                            href={`https://maps.google.com/?q=${detailData.gpsCoordinates}`}
                            isExternal
                            color='blue.500'>
                            {detailData.gpsCoordinates}
                          </Link>
                        }
                        isLoading={isLoading}
                      />
                    )}
                  </SimpleGrid>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>

            {/* Brand License Information */}
            {detailData?.brandLicense && (
              <>
                <Divider />
                <Box>
                  <Text fontSize='lg' fontWeight='bold' mb={4} color='teal.600'>
                    Brand License Mapping
                  </Text>
                  <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                    <DetailRow label='Brand ID' value={detailData.brandLicense.brandId} isLoading={isLoading} />
                    <DetailRow label='License ID' value={detailData.brandLicense.licenceId} isLoading={isLoading} />
                    <DetailRow label='Mapping ID' value={detailData.brandLicense.id} isLoading={isLoading} />
                  </SimpleGrid>
                </Box>
              </>
            )}

            <Divider />

            {/* Timestamps */}
            <Box>
              <Text fontSize='lg' fontWeight='bold' mb={4} color='gray.600'>
                Record Information
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                <DetailRow
                  label='Created At'
                  value={formatDate(detailData?.createdAt)}
                  icon={FiCalendar}
                  isLoading={isLoading}
                />
                <DetailRow
                  label='Updated At'
                  value={formatDate(detailData?.updatedAt)}
                  icon={FiCalendar}
                  isLoading={isLoading}
                />
              </SimpleGrid>
            </Box>
          </VStack>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
